<script setup lang="ts">
import { StaticFile } from "~/classes/file/StaticFile"

const props = defineProps({
  creates: null
})

const { getFileReference } = useAgent()

/** Returns [file, loading, error] */
const fileWithFallback = (item: {
  request: FileCreateRequestDatagram
  response?: FileCreateResponseDatagram
}): [StaticFile, boolean, DatagramError | undefined] => {
  const reference = getFileReference(item.request.file.id)
  if (reference)
    return [reference, !item.response, item.response?.error]
  if (item.response?.file)
    return [new StaticFile(item.response.file), !item.response, item.response?.error]
  return [new StaticFile(item.request.file), !item.response, item.response?.error]
}

// We render audio, video, images, text (normal or web), and links separately from
// the rest of the files that might be created.

const files = computed(() => Array.from(props.creates.values()).map(fileWithFallback))

const previews = computed(() => files.value.filter(
  properties => properties[0].isAudio()
    || properties[0].isText()
    || properties[0].isImage(),
))

const others = computed(() => files.value.filter(
  properties => !properties[0].isAudio()
    && !properties[0].isText()
    && !properties[0].isImage(),
))
</script>

<template>
  <template v-for="[file, loading, error] in previews" :key="file.id">
    <agent-status-preview-audio
      v-if="file.isAudio()"
      :file
      :loading
      :error
    />
    <agent-status-preview-text
      v-else-if="file.isText()"
      :file
      :loading
      :error
    />
    <agent-status-preview-image
      v-else-if="file.isImage()"
      :file
      :loading
      :error
    />
  </template>
  <template v-if="len(others) > 0">
    <agent-status-files
      :files="others"
      :label="`Creating ${others.length} ${others.length === 1 ? 'file' : 'files'}`"
    />
  </template>
</template>
